import api from '@/utils/api';

const DEDUPE_WINDOW_MS = 30_000;
const MAX_EVENTS_PER_MINUTE = 20;
const IGNORED_ERROR_MESSAGE_PATTERNS = [
  /resizeobserver loop completed with undelivered notifications/i,
  /resizeobserver loop limit exceeded/i,
];
const recentEvents = new Map();
const sentTimestamps = [];

const now = () => Date.now();

const cleanup = (timestampMs) => {
  for (const [key, value] of recentEvents.entries()) {
    if (timestampMs - value > DEDUPE_WINDOW_MS) {
      recentEvents.delete(key);
    }
  }

  while (sentTimestamps.length > 0 && timestampMs - sentTimestamps[0] > 60_000) {
    sentTimestamps.shift();
  }
};

const toMessage = (errorLike) => {
  if (!errorLike) return 'Unknown frontend error';
  if (typeof errorLike === 'string') return errorLike;
  if (errorLike?.message) return String(errorLike.message);
  return String(errorLike);
};

const toStack = (errorLike) => {
  if (!errorLike) return '';
  if (typeof errorLike?.stack === 'string') return errorLike.stack;
  return '';
};

const isBootstrapTooltipCleanupError = (message, stack) => (
  /cannot convert undefined or null to object/i.test(toMessage(message))
  && /_isWithActiveTrigger/.test(toStack(stack) || String(stack || ''))
);

const shouldIgnoreFrontendError = (message, stack) => (
  IGNORED_ERROR_MESSAGE_PATTERNS.some((pattern) => pattern.test(toMessage(message)))
  || isBootstrapTooltipCleanupError(message, stack)
);

const postFrontendError = async ({
  message,
  stack,
  component,
  level = 'error',
  extra = {},
}) => {
  if (shouldIgnoreFrontendError(message, stack)) {
    return;
  }

  const timestampMs = now();
  cleanup(timestampMs);

  if (sentTimestamps.length >= MAX_EVENTS_PER_MINUTE) {
    return;
  }

  const dedupeKey = `${level}|${component || ''}|${message || ''}|${stack || ''}`.slice(0, 400);
  if (recentEvents.has(dedupeKey)) {
    return;
  }
  recentEvents.set(dedupeKey, timestampMs);
  sentTimestamps.push(timestampMs);

  try {
    await api.post('/api/logs/', {
      level,
      message: toMessage(message),
      stack: toStack(stack) || stack || '',
      url: window.location?.href || '',
      component: component || '',
      userAgent: navigator.userAgent || '',
      release: 'ui-v2',
      timestamp: new Date(timestampMs).toISOString(),
      extra,
    });
  } catch {
    // Intentionally swallow to avoid recursive error logging.
  }
};

export const installFrontendErrorLogging = (app) => {
  app.config.errorHandler = (err, instance, info) => {
    const componentName = instance?.type?.name || instance?.type?.__name || 'VueComponent';
    postFrontendError({
      message: toMessage(err),
      stack: toStack(err),
      component: componentName,
      extra: {
        source: 'vue.errorHandler',
        info: info || '',
      },
    });
    // Keep default visibility in console for local debugging.
    console.error(err);
  };

  window.addEventListener('error', (event) => {
    postFrontendError({
      message: event?.message || toMessage(event?.error),
      stack: toStack(event?.error),
      component: 'window',
      extra: {
        source: 'window.error',
        filename: event?.filename || '',
        lineno: event?.lineno || null,
        colno: event?.colno || null,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    postFrontendError({
      message: toMessage(reason),
      stack: toStack(reason),
      component: 'promise',
      extra: {
        source: 'window.unhandledrejection',
      },
    });
  });
};
